import { wrap, type Providers } from ".";

/**
 * Takes parents, wraps them with a child, and returns a functional component
 * where the child is wrapped with the parents and the child can be passed props.
 *
 * @param parents The parents to wrap the child in, from outer to inner, either as just the component or a tuple of the component and its props.
 * Must be able to take a `children` prop.
 *
 * @example
 * // writing
 * const Component = provide(OuterWrapper, [InnerWrapper, {otherProp: "Hello"}])(App)
 *
 * // is the same as writing
 * const Component: React.FC<AppProps> = (props) => {
 *    return (
 *      <OuterWrapper>
 *        <InnerWrapper otherProp="Hello">
 *          <App {...props} />
 *        </InnerWrapper>
 *      </OuterWrapper>
 *    );
 * }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic type
export function provide<TParents extends Record<string, any>[]>(
  ...parents: Providers<TParents>
) {
  return function <TProps extends {}>(
    child: React.ComponentType<TProps>,
  ): React.FC<TProps> {
    return function (props) {
      return wrap(...parents)(child, props);
    };
  };
}
